const jwt = require('jsonwebtoken')
const axios = require('axios')
const { get } = require('lodash')
const { promisify } = require('util')
const PassportClientError = require('./PassportClientError')
const ErrorCodes = require('./ErrorCodes')

class PassportClient {
  constructor ({ baseURL, auth, timeout, logger }) {
    this.logger = logger
    this.client = axios.create({
      baseURL,
      auth,
      timeout
    })
  }

  async check ({ token, types, grants }) {
    try {
      const { data } = await this.client.post('/api/v1/security/check', {
        token,
        types,
        grants
      })

      const {
        userId,
        credentialId,
        decodedToken,
        grants: userGrants,
        verificators
      } = data

      return {
        userId,
        credentialId,
        decodedToken,
        grants: userGrants,
        verificators
      }
    } catch (e) {
      const httpStatusCode = get(e, 'response.status')
      const subErrors = describeSubErrors(e)

      if (this.logger && (httpStatusCode == null || httpStatusCode >= 500)) {
        this.logger.error(`passportClient.check.error; ${JSON.stringify(subErrors)}`)
      }

      throw new PassportClientError({
        code: ErrorCodes.PASSPORT_CLIENT_CANNOT_CHECK,
        message: 'Cannot check token',
        httpStatusCode,
        subErrors
      })
    }
  }

  async checkBasic ({
    login,
    password,
    grants
  }) {
    try {
      const { data } = await this.client.post('/api/v1/security/check/basic', {
        login,
        password,
        grants
      })

      const {
        userId,
        credentialId
      } = data

      return {
        userId,
        credentialId
      }
    } catch (e) {
      const httpStatusCode = get(e, 'response.status')
      const subErrors = describeSubErrors(e)

      if (this.logger && (httpStatusCode == null || httpStatusCode >= 500)) {
        this.logger.error(`passportClient.checkBasic.error; ${JSON.stringify(subErrors)}`)
      }

      throw new PassportClientError({
        code: ErrorCodes.PASSPORT_CLIENT_CANNOT_CHECK_BASIC,
        message: 'Cannot check basic credentials',
        httpStatusCode,
        subErrors
      })
    }
  }

  async checkOTP ({
    userId,
    type,
    otp,
    requestBody
  }) {
    try {
      const { data } = await this.client.post('/api/v1/security/check/otp', {
        userId,
        type,
        otp,
        requestBody
      })
      return data
    } catch (e) {
      const httpStatusCode = get(e, 'response.status')
      const subErrors = describeSubErrors(e)

      if (this.logger && (httpStatusCode == null || httpStatusCode >= 500)) {
        this.logger.error(`passportClient.checkOTP.error; ${JSON.stringify(subErrors)}`)
      }

      throw new PassportClientError({
        code: ErrorCodes.PASSPORT_CLIENT_CANNOT_CHECK_OTP,
        message: 'Verification by OTP failed',
        httpStatusCode,
        subErrors
      })
    }
  }

  async decodeAndVerifyToken ({ token, pubKey, clientId }) {
    const decoded = await promisify(jwt.verify)(token, pubKey)

    if (decoded.iss !== 'passport') {
      throw new PassportClientError({
        code: ErrorCodes.PASSPORT_CLIENT_INCORRECT_ISSUER,
        mesage: 'Incorrect issuer',
        httpStatusCode: 403
      })
    }

    const isPassportAud = decoded.aud === 'passport'
    const isClientAud = clientId != null && decoded.aud === `urn:${clientId}`

    if (!isPassportAud && !isClientAud) {
      throw new PassportClientError({
        code: ErrorCodes.PASSPORT_CLIENT_INCORRECT_AUDIENCE,
        mesage: 'Incorrect audience',
        httpStatusCode: 403
      })
    }

    const [userId, credentialId] = decoded.sub.split(':')

    return {
      userId,
      credentialId,
      type: decoded.type,
      extra: decoded.extra, // Custom extra data from JWT token
      issuedAt: decoded.iat,
      expirationTime: decoded.exp
    }
  }

  async validateAcknowledge ({ token, grants, method, path, query, body }) {
    try {
      const { data } = await this.client.post('/api/v1/security/acknowledge/validate', {
        token,
        grants,

        method,
        path,
        query,
        body
      })

      const {
        userId
      } = data

      return {
        userId
      }
    } catch (e) {
      const httpStatusCode = get(e, 'response.status')
      const subErrors = describeSubErrors(e)

      if (this.logger && (httpStatusCode == null || httpStatusCode >= 500)) {
        this.logger.error(`passportClient.validateAcknowledge.error; ${JSON.stringify(subErrors)}`)
      }

      throw new PassportClientError({
        code: ErrorCodes.PASSPORT_CLIENT_CANNOT_CHECK,
        message: 'Cannot check token',
        httpStatusCode,
        subErrors
      })
    }
  }
}

function describeSubErrors (...exceptions) {
  const subErrors = exceptions
    .map(e => {
      if (e.response instanceof Object &&
          e.response.data instanceof Object &&
          e.response.data.error instanceof Object
      ) {
        return e.response.data.error
      }
      return {
        message: e.message
      }
    })
    .filter(error => error != null)
  return subErrors.length > 0
    ? subErrors
    : null
}

module.exports = PassportClient



//////////////////
// WEBPACK FOOTER
// ./node_modules/@chronobank/passport.client/lib/PassportClient.js
// module id = JrPm
// module chunks = 0